import { DynamicPageVersion, WithId } from '@betterfunnel/types';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronLeftIcon } from '@heroicons/react/24/solid';
import { useVirtualizer } from '@tanstack/react-virtual';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useContext, useEffect, useRef, useState } from 'react';
import Badge from '../components/Common/Badge';
import IconButton from '../components/Common/IconButton';
import Loader from '../components/Common/Loader';
import MenuButton from '../components/Common/MenuButton';
import Tooltip from '../components/Common/Tooltip';
import { DashboardContext } from '../contexts/DashboardContext';
import { UserContext } from '../contexts/UserContext';
import { getDynamicPageVersions } from '../services/DynamicPagesService';
import { DynamicPageVersionDetailsForm } from './PublishDynamicPageVersionDrawer';

dayjs.extend(relativeTime);

const PAGE_SIZE = 50;

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function DynamicPageVersionsHistoryDrawer({
  open,
  onClose,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useContext(DashboardContext);

  const [versions, setVersions] = useState<WithId<DynamicPageVersion>[]>();
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);

  const [selectedVersion, setSelectedVersion] =
    useState<WithId<DynamicPageVersion>>();

  const [error, setError] = useState<Error>();

  useEffect(() => {
    setError(undefined);
    setVersions(undefined);

    if (!open) return;
    if (!selectedAccount) return;
    if (!selectedDynamicPage) return;

    setIsLoadingVersions(true);

    let relevant = true;
    getDynamicPageVersions(
      selectedAccount.id,
      selectedDynamicPage.id,
      PAGE_SIZE,
    )
      .then((versionDocs) => {
        if (!relevant) return;
        setVersions(versionDocs.map((doc) => ({ ...doc.data(), id: doc.id })));
      })
      .finally(() => {
        if (!relevant) return;
        setIsLoadingVersions(false);
      });

    return () => {
      relevant = false;
    };
  }, [open, selectedAccount, selectedDynamicPage]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <div className="fixed inset-0" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-16">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-2xl transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <form className="flex h-full flex-col bg-white shadow-xl">
                <div className="flex flex-1 flex-col">
                  {/* Header */}
                  <div className="bg-gray-50 px-4 py-6 sm:px-6">
                    <div className="flex items-start justify-between space-x-3">
                      {selectedVersion && (
                        <div className="flex h-7 items-center">
                          <IconButton
                            variant="ternary"
                            onClick={() => setSelectedVersion(undefined)}
                          >
                            <ChevronLeftIcon
                              aria-hidden="true"
                              className="size-6"
                            />
                          </IconButton>
                        </div>
                      )}
                      <div className="flex-1 space-y-1">
                        <DialogTitle className="text-base font-semibold text-gray-900">
                          Versions History
                        </DialogTitle>
                        {/* <p className="text-sm text-gray-500">
                          Publish a new version of the dynamic page to make it
                          live.
                        </p> */}
                      </div>
                      <div className="flex h-7 items-center">
                        <IconButton variant="ternary" onClick={() => onClose()}>
                          <XMarkIcon aria-hidden="true" className="size-6" />
                        </IconButton>
                      </div>
                    </div>
                  </div>

                  {/* Divider container */}
                  {!selectedVersion && (
                    <div className="flex-1 space-y-6 py-6 sm:space-y-0 sm:divide-y sm:divide-gray-200 sm:py-0">
                      {/* Versions list */}
                      <div className="relative flex h-full flex-col divide-y divide-gray-200">
                        {isLoadingVersions && (
                          <div className="flex h-32 items-center justify-center">
                            <Loader />
                          </div>
                        )}
                        {/* {!isLoadingVersions && versions?.length === 0 && (
                          <div className="flex h-32 items-center justify-center">
                            <p className="text-sm text-gray-500">
                              No versions found, created at{' '}
                              {selectedDynamicPage
                                ?.data()
                                .createdAt.toDate()
                                .toLocaleString()}
                              .
                            </p>
                          </div>
                        )} */}

                        {!isLoadingVersions && (
                          <div className="relative block w-full flex-1">
                            <div className="absolute inset-0">
                              <VersionsHistoryList
                                versions={versions ?? []}
                                onSelect={(version) => {
                                  setSelectedVersion(version);
                                }}
                              />
                            </div>
                          </div>
                        )}

                        {error && (
                          <div className="flex h-32 items-center justify-center">
                            <p className="text-sm text-red-500">
                              {error.message}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedVersion && (
                    <DynamicPageVersionDetailsForm
                      version={selectedVersion}
                      readOnly
                      hasPrevVersion={
                        (versions?.indexOf(selectedVersion) ?? 0) + 1 <
                        (versions?.length ?? 0)
                      }
                    />
                  )}
                </div>

                {/* Action buttons */}
                {/* <div className="shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                  <div className="flex justify-end space-x-3">
                    <Button variant="secondary" onClick={() => onClose()}>
                      Cancel
                    </Button>
                    <Button
                      loading={isLoadingPublish}
                      variant="primary"
                      disabled={isPublishButtonDusabled}
                      onClick={onPublishVersionButtonClick}
                    >
                      Publish
                    </Button>
                  </div>
                </div> */}
              </form>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

interface VersionsHistoryListProps {
  versions: WithId<DynamicPageVersion>[];
  onSelect: (version: WithId<DynamicPageVersion>) => void;
}

const VersionsHistoryList = ({
  versions,
  onSelect,
}: VersionsHistoryListProps) => {
  const versionsListContainerRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: (versions?.length ?? 0) + 1,
    estimateSize: () => 80, // estimate row height for accurate scrollbar dragging
    getScrollElement: () => versionsListContainerRef.current,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div
      ref={versionsListContainerRef}
      className="h-full w-full overflow-y-auto contain-strict"
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
          }}
        >
          {virtualItems.map((virtualRow) => (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
            >
              {virtualRow.index === versions.length ? (
                <CreationHistoryItem />
              ) : (
                <VersionsHistoryItem
                  version={versions[virtualRow.index]}
                  index={virtualRow.index}
                  onSelect={onSelect}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface VersionsHistoryItemProps {
  version: WithId<DynamicPageVersion>;
  index: number;
  onSelect: (version: WithId<DynamicPageVersion>) => void;
}

const VersionsHistoryItem = ({
  version,
  index,
  onSelect,
}: VersionsHistoryItemProps) => {
  const { teamMembers } = useContext(DashboardContext);

  return (
    <div
      key={version.id}
      className="flex cursor-pointer items-center justify-between gap-2 px-6 py-4 hover:bg-gray-50 sm:py-5"
      onClick={() => onSelect(version)}
    >
      <div className="flex items-center space-x-3">
        <div>
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium text-gray-900">
              Version: {version.name}
            </p>
            {index === 0 && <Badge title="Current" />}
          </div>
          {version.description && (
            <p className="line-clamp-2 text-sm text-gray-500">
              {version.description}
            </p>
          )}

          <p className="line-clamp-2 text-sm text-gray-500">
            Created By:
            {teamMembers?.find((t) => t.id === version.createdBy)?.data()
              .email ?? 'Unknown'}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center space-x-3">
        <Tooltip
          placement="left"
          text={version.createdAt.toDate().toLocaleString()}
        >
          <p className="line-clamp-2 text-sm text-gray-500">
            {dayjs(version.createdAt.toDate()).fromNow()}
          </p>
        </Tooltip>
        <MenuButton
          items={[
            {
              title: 'View',
              onClick: () => {
                onSelect(version);
              },
            },
            ...(index === 0
              ? []
              : [
                  {
                    destructive: true,
                    title: 'Rollback (coming soon)',
                    disabled: true,
                    onClick: undefined,
                  },
                ]),
          ]}
        />
      </div>
    </div>
  );
};

const CreationHistoryItem = () => {
  const { teamMembers, selectedDynamicPage } = useContext(DashboardContext);

  return (
    <div
      key={selectedDynamicPage?.id}
      className="flex items-center justify-between gap-2 px-6 py-4 hover:bg-gray-50 sm:py-5"
    >
      <div className="flex items-center space-x-3">
        <div>
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium text-gray-900">
              Created at{' '}
              {selectedDynamicPage
                ?.data()
                .createdAt.toDate()
                .toLocaleDateString()}
            </p>
            {/* {index === 0 && <Badge title="Current" />} */}
          </div>

          <p className="line-clamp-2 text-sm text-gray-500">
            Created By:
            {teamMembers
              ?.find((t) => t.id === selectedDynamicPage?.data().createdBy)
              ?.data().email ?? 'Unknown'}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center space-x-3">
        <Tooltip
          placement="left"
          text={selectedDynamicPage?.data().createdAt.toDate().toLocaleString()}
        >
          <p className="line-clamp-2 text-sm text-gray-500">
            {dayjs(selectedDynamicPage?.data().createdAt.toDate()).fromNow()}
          </p>
        </Tooltip>
      </div>
    </div>
  );
};
